import { useState, useEffect } from 'react';

export const useServiceWorker = ({ onUpdate, onOffline, onOnline }) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  const checkNetworkStatus = () => {
    const handleOnline = () => {
      setIsOffline(false);
      onOnline?.();
    };

    const handleOffline = () => {
      setIsOffline(true);
      onOffline?.();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  };

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const swRegistration = await navigator.serviceWorker.register('/sw.js');
        setRegistration(swRegistration);

        // Escuchar por nuevas versiones
        swRegistration.addEventListener('updatefound', () => {
          const newWorker = swRegistration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  setIsUpdateAvailable(true);
                  onUpdate?.();
                }
              }
            });
          }
        });

        // Verificar actualizaciones periódicamente
        setInterval(
          () => {
            swRegistration.update();
          },
          10 * 60 * 1000
        );

        // Habilitar skip waiting cuando hay un nuevo SW
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SKIP_WAITING') {
            window.location.reload();
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('SW registration failed:', error);
      }
    }
  };

  const getCacheSize = () => {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      navigator.serviceWorker.controller?.postMessage(
        { type: 'GET_CACHE_SIZE' },
        [messageChannel.port2]
      );
    });
  };

  const clearCache = () => {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_CACHE' }, [
        messageChannel.port2,
      ]);
    });
  };

  useEffect(() => {
    registerServiceWorker();
    const cleanup = checkNetworkStatus();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOffline,
    isUpdateAvailable,
    registration,
    getCacheSize,
    clearCache,
  };
};

export default useServiceWorker;
